import type { Row } from "./api";

/** Questions describe circumstances to capture, not an eligibility determination. */
export const interviewItemLabels: Record<string, string> = {
  "RH-FILED":
    "Has a federal return for 2025 already been filed for this taxpayer?",
  "RH-AMENDED":
    "Are you amending or replacing a return that was already filed?",
  "RH-DECEDENT": "Is this a return for a taxpayer who died?",
  "RH-BANKRUPTCY":
    "Is there a bankruptcy estate or another bankruptcy matter affecting this return?",
  "RH-RELIEF":
    "Is disaster, military, deadline, loss, repayment, or other special tax relief relevant to this return?",
  "RH-REFUND-LIMIT":
    "Is there a question about an earlier filing or the deadline to claim a refund?",
  "SC-MARRIAGE":
    "Was the taxpayer treated as married, or does their marital status need clarification?",
  "SC-SPOUSE-DEATH":
    "Is there a spouse’s death that needs to be considered for this return?",
  "SC-HOUSEHOLD":
    "Could the household circumstances support Head of Household or Qualifying Surviving Spouse?",
  "SC-RDP-COMMUNITY":
    "Is there a registered domestic partnership or shared income or property requiring community-property review?",
  "SC-DEPENDENCY":
    "Is there a dependency, household, support, custody, or shared-support matter to review, including whether someone can claim the taxpayer?",
  "OI-INTEREST": "Did the taxpayer receive taxable or tax-exempt interest?",
  "OI-DIVIDEND": "Did the taxpayer receive ordinary or qualified dividends?",
  "OI-RETIREMENT":
    "Did the taxpayer receive an IRA, pension, annuity, or other retirement distribution?",
  "OI-SS-RR":
    "Did the taxpayer receive Social Security or Railroad Retirement benefits?",
  "OI-CAPITAL-DIGITAL":
    "Were there investment or other capital transactions, broker proceeds, or digital-asset transactions?",
  "OI-BUSINESS":
    "Did the taxpayer receive self-employment, gig-work, business, or hobby payments?",
  "OI-RENTAL-K1":
    "Were there rental, royalty, partnership, S corporation, estate, trust, or Schedule K-1 items?",
  "OI-FARM": "Did the taxpayer have farm income?",
  "OI-UNEMPLOYMENT-ALIMONY-REFUND":
    "Did the taxpayer receive unemployment benefits, a potentially taxable state refund, or potentially taxable alimony?",
  "OI-GAMBLING-OTHER":
    "Did the taxpayer receive gambling winnings, prizes, awards, jury-duty pay, or other income?",
  "OI-COD": "Was any debt canceled, or was there a foreclosure?",
  "OI-FOREIGN":
    "Did the taxpayer receive wages or other income from outside the United States?",
  "OI-GIFT-INHERIT-LIFE":
    "Did the taxpayer receive a gift, inheritance, or life-insurance payment, or have a related question about excluding income?",
  "OI-HOME-SALE": "Did the taxpayer sell or exchange a home?",
  "OI-UNREPORTED":
    "Was there income or a reportable transaction for which no tax form was received?",
  "WB-TIPS":
    "Were there tips, including allocated or unreported tips, or a possible tip-related credit to review?",
  "WB-OVERTIME":
    "Was there overtime pay or a possible overtime deduction to review?",
  "WB-SICK-PAY": "Did an insurer or another third party pay sick pay?",
  "WB-STATUTORY": "Was the taxpayer treated as a statutory employee?",
  "WB-EQUITY":
    "Were there stock options, option exercises, restricted stock units, employee stock purchases, or other equity compensation?",
  "WB-FOREIGN-EXCLUSION":
    "Is there a possible foreign earned-income or housing exclusion to review?",
  "WB-EMPLOYER-EDUCATION":
    "Were there employer education benefits, scholarships, fellowships, or other education assistance?",
  "WB-OTHER-FRINGE":
    "Were there other employer benefits, fringe benefits, wage exclusions, clergy or combat pay, or other special wage treatment?",
  "AJ-EDUCATOR": "Did the taxpayer have educator expenses to review?",
  "AJ-HSA-MSA":
    "Were there HSA or Archer MSA contributions, distributions, or coverage details to review?",
  "AJ-SELF-EMPLOYED":
    "Are self-employed tax, retirement contributions, or health-insurance deductions relevant?",
  "AJ-IRA":
    "Were there traditional, SEP, SIMPLE, Roth, or other IRA contributions or recharacterizations?",
  "AJ-STUDENT-LOAN": "Did the taxpayer pay student-loan interest?",
  "AJ-OTHER":
    "Were there alimony payments, moving expenses, repaid jury-duty pay, penalties, or other adjustments to income?",
  "S1A-COMMON":
    "Are there eligibility or income-limit details for the additional deductions to review?",
  "S1A-TIPS": "Is a deduction for qualified tips a possibility to review?",
  "S1A-OVERTIME":
    "Is a deduction for qualified overtime a possibility to review?",
  "S1A-VEHICLE":
    "Is there passenger-vehicle loan interest that may qualify for a deduction?",
  "S1A-SENIOR":
    "Is the additional deduction for seniors a possibility to review?",
  "IT-MEDICAL": "Did the taxpayer pay medical or dental expenses?",
  "IT-SALT":
    "Did the taxpayer pay state or local income, sales, real-estate, or personal-property taxes?",
  "IT-INTEREST": "Did the taxpayer pay home-mortgage or investment interest?",
  "IT-CHARITY-CASH": "Did the taxpayer make cash charitable contributions?",
  "IT-CHARITY-NONCASH":
    "Did the taxpayer donate property or make other noncash charitable contributions?",
  "IT-CASUALTY": "Did the taxpayer have a casualty, theft, or disaster loss?",
  "IT-OTHER":
    "Are there other itemized expenses, repayments, gambling losses, estate-tax deductions, or special deduction matters to review?",
  "IT-CARRYOVER":
    "Are there unused charitable or other itemized deductions carried over from an earlier year?",
  "CR-CTC-ODC":
    "Is a child tax credit, additional child tax credit, or credit for other dependents a possibility to review?",
  "CR-EITC": "Is the earned income credit a possibility to review?",
  "CR-DEPENDENT-CARE":
    "Is a credit for child or dependent care, including qualifying care for a spouse, a possibility to review?",
  "CR-AOC":
    "Is an American opportunity education credit a possibility to review?",
  "CR-LLC": "Is a lifetime learning education credit a possibility to review?",
  "CR-SAVER":
    "Is a retirement savings contributions credit a possibility to review?",
  "CR-PTC":
    "Was there Marketplace health coverage, a Form 1095-A, or a possible premium tax credit to review?",
  "CR-FOREIGN":
    "Were foreign taxes paid, or is a foreign tax credit a possibility to review?",
  "CR-ENERGY":
    "Is a residential clean-energy or energy-efficient home-improvement credit a possibility to review?",
  "CR-CLEAN-VEHICLE":
    "Is a credit for a new, used, or commercial clean vehicle a possibility to review?",
  "CR-ELDERLY-DISABLED":
    "Is the credit for the elderly or permanently and totally disabled a possibility to review?",
  "CR-ADOPTION":
    "Were there adoption expenses, or is an adoption credit a possibility to review?",
  "CR-OTHER-NONREF": "Is there another nonrefundable tax credit to review?",
  "CR-OTHER-REF": "Is there another refundable tax credit to review?",
  "CR-GENERAL-BUSINESS":
    "Is there a general business credit or a credit carryforward to review?",
  "OT-SELF-EMPLOYMENT": "Does self-employment tax need to be considered?",
  "OT-ADDITIONAL-MEDICARE":
    "Does Additional Medicare Tax or tax on unreported tips need to be considered?",
  "OT-NIIT": "Does tax on net investment income need to be considered?",
  "OT-HOUSEHOLD": "Do household employment taxes need to be considered?",
  "OT-RETIREMENT-HSA":
    "Does an additional tax involving retirement, health savings, medical savings, education, or a similar account need to be considered?",
  "OT-RECAPTURE-SPECIAL":
    "Does repayment of an earlier tax benefit, alternative minimum tax, excess advance credit, or another special tax need to be considered?",
  "PY-ESTIMATED": "Did the taxpayer make estimated tax payments?",
  "PY-EXTENSION": "Did the taxpayer make a payment with an extension request?",
  "PY-PRIOR-APPLIED":
    "Was a prior-year overpayment applied to the taxpayer’s 2025 tax?",
  "PY-OTHER":
    "Is there another payment, refundable credit, or excess withholding claim to review?",
  "CE-NOL": "Is there an unused net operating loss from an earlier year?",
  "CE-CAPITAL": "Is there an unused capital loss from an earlier year?",
  "CE-PASSIVE":
    "Is there a passive-activity or at-risk amount carried over from an earlier year?",
  "CE-ELECTION":
    "Is there a tax election, revocation, special accounting method, or earlier-year election to review?",
  "CE-INJURED-INNOCENT":
    "Is there an injured-spouse, innocent-spouse, allocation, or joint-liability relief matter to review?",
  "ITL-RESIDENCY":
    "Is there a nonresident, part-year tax-residency, treaty, expatriation, or foreign-residence matter to review?",
  "ITL-ASSET":
    "Are there foreign accounts, assets, trusts, taxes, or international reporting requirements to review?",
  "ITL-TERRITORY":
    "Is there income, residence, or a filing requirement involving Puerto Rico or another U.S. territory?",
  "XD-1099-1098":
    "Are there any Forms 1099 or 1098, brokerage statements, or other income or deduction information returns?",
  "XD-1095A-SSA-RRB-K1":
    "Are there Forms 1095-A, SSA-1099, RRB-1099, Schedules K-1, or similar special statements?",
  "XD-OTHER":
    "Is there any other tax-related document outside the complete W-2 document list?",
};

const interviewFieldLabels: Record<string, string> = {
  items: "About this topic",
  return_kind: "Type of return being prepared",
  accounting_period: "Period covered by the return",
  decedent_case: "Is this a return for a taxpayer who died?",
  bankruptcy_case: "Is there a bankruptcy matter affecting this return?",
  special_relief_case:
    "Is special tax relief, such as disaster or military relief, relevant to this return?",
  claimable_status: "Can another person claim the taxpayer as a dependent?",
  dependent_count: "Number of dependents on this return",
  qualifying_person_count:
    "Number of qualifying people to consider for filing status",
};

const object = (value: unknown): value is Row =>
  value !== null && typeof value === "object" && !Array.isArray(value);

function clone<T>(value: T): T {
  if (Array.isArray(value)) return value.map((item) => clone(item)) as T;
  if (object(value))
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, clone(item)]),
    ) as T;
  return value;
}

/** Detached UI annotation only. No values, constraints, references, or source objects are rewritten. */
export function applyInterviewCopy(schema: Row): Row {
  const result = clone(schema);
  const visit = (node: unknown): void => {
    if (!object(node)) return;
    if (object(node.properties)) {
      for (const [key, child] of Object.entries(node.properties)) {
        if (!object(child)) continue;
        const label = interviewItemLabels[key] ?? interviewFieldLabels[key];
        if (label) {
          child.title = label;
          child["x-intake-label"] = label;
        }
        visit(child);
      }
    }
    for (const key of [
      "$defs",
      "definitions",
      "patternProperties",
      "dependentSchemas",
    ]) {
      if (object(node[key])) Object.values(node[key]).forEach(visit);
    }
    if (object(node.dependencies))
      Object.values(node.dependencies).forEach(visit);
    for (const key of ["allOf", "anyOf", "oneOf", "prefixItems"]) {
      if (Array.isArray(node[key])) node[key].forEach(visit);
    }
    for (const key of [
      "items",
      "additionalItems",
      "contains",
      "not",
      "if",
      "then",
      "else",
      "additionalProperties",
      "unevaluatedProperties",
      "unevaluatedItems",
      "propertyNames",
    ]) {
      if (Array.isArray(node[key])) node[key].forEach(visit);
      else visit(node[key]);
    }
    // This annotation drives the natural-input renderer independently of anyOf.
    if (object(node["x-intake-stateful"]))
      visit(node["x-intake-stateful"].valueSchema);
  };
  visit(result);
  return result;
}
